import { useState } from 'react';
import Button from '../base/Button';
import { logger } from '../../utils/logger';
import toast from '../../utils/toast';
import { useWebViewDetection } from '../../hooks/useWebViewDetection';

type OpenInBrowserButtonProps = {
  variant?: 'primary' | 'secondary';
  className?: string;
};
export default function OpenInBrowserButton({
  variant = 'secondary',
  className = '',
}: OpenInBrowserButtonProps) {
  const [isSharing, setIsSharing] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const { isStandalone, isWebView } = useWebViewDetection();

  if (isStandalone) return null;

  const handleShare = async () => {
    const homeUrl = window.location.origin;
    const hasShare = !!navigator.share;
    const userAgent = navigator.userAgent || '';
    const isAndroid = /Android/i.test(userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(userAgent);

    logger.info('[OpenInBrowserButton] Iniciando compartilhamento', {
      isWebView,
      hasNavigatorShare: hasShare,
      isAndroid,
      isIOS,
    });

    setShowIOSInstructions(false);

    // iOS WebView → Copiar link automaticamente + instruções visuais
    if (isIOS && isWebView) {
      try {
        setIsSharing(true);

        await fallbackCopy(homeUrl, 'Link copiado! Veja as instruções abaixo.');

        setShowIOSInstructions(true);
        logger.info('[OpenInBrowserButton] Instruções iOS exibidas após copiar link', {
          isWebView,
        });
      } catch (error) {
        logger.error('[OpenInBrowserButton] Falha ao preparar instruções iOS', error);
        toast.error('Não foi possível copiar o link automaticamente. Tente novamente.');
      } finally {
        setIsSharing(false);
      }
      return;
    }

    // WebView Android SEM navigator.share → usar Intent URL
    if (isWebView && isAndroid && !hasShare) {
      try {
        setIsSharing(true);

        
        // Intent URL força abertura no navegador padrão do Android
        const intentUrl = `intent://${homeUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
        
        logger.info('[OpenInBrowserButton] Usando Intent URL para Android WebView', { intentUrl });
        window.location.href = intentUrl;
        
        // Instagram/Facebook mostram diálogo nativo de confirmação
        // Resetar estado após navegação
        setTimeout(() => setIsSharing(false), 1000);
        return;
      } catch (error) {
        logger.error('[OpenInBrowserButton] Intent URL falhou', error);
        setIsSharing(false);
        await fallbackCopy(homeUrl);
        return;
      }
    }

    // Navegadores com Web Share API
    if (hasShare) {
      try {
        setIsSharing(true);
        logger.info('[OpenInBrowserButton] Tentando navigator.share...');
        
        await navigator.share({
          title: 'Magistral Homeopatia',
          text: 'Abra no navegador principal para continuar:',
          url: homeUrl,
        });
        
        logger.info('[OpenInBrowserButton] navigator.share SUCESSO');
        toast.success('Agora abra o link no navegador principal!');
      } catch (error) {
        const errorName = (error as Error)?.name;
        const errorMessage = (error as Error)?.message;
        
        logger.error('[OpenInBrowserButton] navigator.share ERRO', {
          errorName,
          errorMessage,
          isWebView,
          fullError: error,
        });
        
        // AbortError = usuário cancelou → não fazer nada
        if (errorName !== 'AbortError') {
          logger.warn('[OpenInBrowserButton] Erro não é AbortError, usando fallback');
          await fallbackCopy(homeUrl);
        } else {
          logger.info('[OpenInBrowserButton] Usuário cancelou compartilhamento');
        }
      } finally {
        setIsSharing(false);
      }
    } else {
      // Último recurso: copiar para área de transferência
      logger.warn('[OpenInBrowserButton] navigator.share NÃO disponível, usando fallback');
      await fallbackCopy(homeUrl);
    }
  };

  const fallbackCopy = async (url: string, successMessage = 'Link copiado! Cole no navegador principal.') => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      toast.success(successMessage);
      logger.info('[OpenInBrowserButton] URL copiada para a área de transferência');
    } catch (error) {
      logger.error('[OpenInBrowserButton] Falha ao copiar URL', error);
      toast.error('Não foi possível copiar o link. Tente manualmente.');
    }
  };

  return (
    <div className="w-full">
      <Button
        onClick={handleShare}
        variant={variant}
        className={`w-full ${className}`}
        disabled={isSharing}
      >
        <i className="ri-share-line mr-2"></i>
        {isSharing ? 'Compartilhando...' : 'Abrir no navegador principal'}
      </Button>

      {showIOSInstructions && (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-4 text-left shadow-card">
          <p className="text-sm font-semibold text-blue-900 mb-2">✅ Link copiado! Agora siga estas instruções no iPhone:</p>
          <div className="space-y-3 text-sm text-blue-900/80">
            <div>
              <p className="font-medium text-blue-900">Opção rápida (recomendada)</p>
              <ol className="mt-1 space-y-1 list-decimal list-inside">
                <li>Toque no menu <strong>(⋯)</strong> no canto superior direito.</li>
                <li>
                  Escolha{' '}
                  <i className="ri-share-box-line align-middle text-blue-900 text-lg" aria-hidden></i>{' '}
                  <strong>"Abrir no navegador externo"</strong>.
                </li>
              </ol>
            </div>
            <div className="border-t border-blue-100 pt-3">
              <p className="font-medium text-blue-900">Opção manual</p>
              <ol className="mt-1 space-y-1 list-decimal list-inside">
                <li>Abra o aplicativo <strong>Safari</strong>.</li>
                <li>Toque na barra de endereço e selecione <strong>Colar</strong>.</li>
                <li>Pressione <strong>Ir</strong> para acessar o Magistral.</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
